
import { useState } from "react";
import { motion } from "framer-motion";
import { Avatar } from "@/components/ui/Avatar";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { useToast } from "@/components/ui/ToastProvider";
import { formatCount } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { Link } from "react-router-dom";

interface ProfileHeaderProps {
  profile: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    banner_url?: string | null;
    bio: string | null;
    website?: string | null;
    location?: string | null;
    is_verified: boolean;
    karma_score: number;
    follower_count: number;
    following_count: number;
    role: string;
  };
  isOwnProfile: boolean;
  isFollowing: boolean;
  currentUserId: string | null;
}

export function ProfileHeader({ profile, isOwnProfile, isFollowing: initialFollowing, currentUserId }: ProfileHeaderProps) {
  const [following, setFollowing] = useState(initialFollowing);
  const [followerCount, setFollowerCount] = useState(profile.follower_count);
  const [isLoading, setIsLoading] = useState(false);
  const { success, info } = useToast();

  const handleFollow = async () => {
    if (!currentUserId) {
      info("Sign in to follow", "Create an account to follow users.");
      return;
    }
    if (isLoading) return;
    setIsLoading(true);
    const prev = following;
    setFollowing(!prev);
    setFollowerCount((n) => n + (prev ? -1 : 1));
    try {
      const res = await fetch(`/api/users/${profile.id}/follow`, {
        method: prev ? "DELETE" : "POST",
      });
      if (!res.ok) {
        setFollowing(prev);
        setFollowerCount((n) => n + (prev ? 1 : -1));
      } else if (!prev) {
        success(`Following ${profile.display_name}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const roleBadge = profile.role === "admin"
    ? { label: "Admin", cls: "badge-destructive" }
    : profile.role === "moderator"
    ? { label: "Mod", cls: "badge-accent" }
    : null;

  return (
    <div className="card mb-4 overflow-hidden">
      {/* ── Banner ── */}
      <div className="relative h-36 sm:h-44 bg-gradient-brand">
        {profile.banner_url && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={profile.banner_url}
            alt="Profile banner"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* gradient overlay so avatar pops */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>

      {/* ── Avatar row — sits BELOW the banner, overlapping upward ── */}
      <div className="px-5 pb-5">
        <div className="flex items-end justify-between -mt-10 sm:-mt-12 mb-4">
          {/* Avatar with white ring so it stands out from banner */}
          <div className="relative flex-shrink-0">
            <Avatar
              src={profile.avatar_url}
              alt={profile.display_name}
              size="xl"
              className="ring-4 ring-card shadow-lg"
            />
          </div>

          {/* Action buttons aligned to the right of the avatar row */}
          <div className="flex items-center gap-2 pb-1">
            {isOwnProfile ? (
              <Link to="/main/settings" className="btn-secondary btn-sm">
                Edit profile
              </Link>
            ) : (
              <motion.button
                onClick={handleFollow}
                whileTap={{ scale: 0.96 }}
                disabled={isLoading}
                className={cn(
                  "btn-sm transition-all",
                  following ? "btn-secondary" : "btn-primary"
                )}
              >
                {following ? "Following" : "Follow"}
              </motion.button>
            )}
          </div>
        </div>

        {/* ── Name / badges ── */}
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold text-foreground">{profile.display_name}</h1>
          {profile.is_verified && <VerifiedBadge size="md" />}
          {roleBadge && (
            <span className={cn("badge text-2xs", roleBadge.cls)}>
              {roleBadge.label}
            </span>
          )}
        </div>
        <p className="text-sm text-text-muted mt-0.5">@{profile.username}</p>

        {/* ── Bio ── */}
        {profile.bio && (
          <p className="text-sm text-foreground mt-3 leading-relaxed">{profile.bio}</p>
        )}

        {/* ── Meta (website / location) ── */}
        {(profile.website || profile.location) && (
          <div className="flex flex-wrap gap-3 mt-2">
            {profile.location && (
              <span className="text-xs text-text-muted flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {profile.location}
              </span>
            )}
            {profile.website && (
              <a
                href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand hover:underline flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                {profile.website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        )}

        {/* ── Follow stats ── */}
        <div className="flex items-center gap-4 mt-4">
          <button className="flex items-center gap-1.5 group">
            <span className="font-bold text-foreground text-sm group-hover:text-brand transition-colors">
              {formatCount(followerCount)}
            </span>
            <span className="text-xs text-text-muted group-hover:text-text-subtle">followers</span>
          </button>
          <button className="flex items-center gap-1.5 group">
            <span className="font-bold text-foreground text-sm group-hover:text-brand transition-colors">
              {formatCount(profile.following_count)}
            </span>
            <span className="text-xs text-text-muted group-hover:text-text-subtle">following</span>
          </button>
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-foreground text-sm">{formatCount(profile.karma_score)}</span>
            <span className="text-xs text-text-muted">karma</span>
          </div>
        </div>
      </div>
    </div>
  );
}
